import React from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import {
  REVIEW_LINK_TEXT,
  reviewDataSourcesHref,
  reviewRequiredDatasets,
  resaiReviewNotice,
  automationReviewNotice,
} from '../../../utils/reviewRequiredNotice';

// PRD-1359 — one banner, three placements: above Sources used in ResAI, above
// the dataset select on wizard step 2, and again under Review on step 4.
//
// It is the ONLY signal that an answer (or a scheduled run) was computed on
// partial data, so it is required rather than cosmetic — don't make it
// dismissible. It is also a warning and never a block: nothing here disables a
// control.
//
// NOTE — deliberate deviation from "gate every new feature with useFlag()",
// following the precedent already set for the Review entry point in
// DataSources.jsx (see the `hitl-field-review` declaration there). This banner
// is gated on `file_counts.awaiting_review` instead. The flag controls whether
// the backend PARKS documents at all, so with it off the count is always 0 and
// this renders nothing anyway. But if the flag is switched off while documents
// are already parked, those documents stay parked — and a flag-gated banner
// would then leave every answer computed on partial data with no warning at
// all, which is the strand bug wearing a worse hat. The count is the correct
// gate.
//
// The alert variant is amber-400 on black text, which is what the frames show;
// `.resui-banner--alert` already carries exactly that, and the inline link is
// `.resui-link`. This component therefore ships no stylesheet of its own —
// there is nothing here the design system does not already define.

const NOTICE = {
  resai: resaiReviewNotice,
  automation: automationReviewNotice,
};

/**
 * @param {object[]} datasets datasets in play — filtered here, so callers can
 *   pass the whole selection without pre-checking.
 * @param {'resai'|'automation'} variant which tense of the copy to use.
 * @param {string} [className] extra spacing class for the host layout.
 */
const ReviewRequiredBanner = ({ datasets, variant = 'resai', className = '' }) => {
  const text = (NOTICE[variant] || resaiReviewNotice)(datasets);
  if (!text) return null;

  // Deep-link to the single parked dataset when there is exactly one; otherwise
  // just the filtered list, since there is no one dataset to open.
  const parked = reviewRequiredDatasets(datasets);
  const href = reviewDataSourcesHref(parked.length === 1 ? parked[0].id : undefined);

  return (
    <div className={`resui-banner resui-banner--alert review-required-banner ${className}`.trim()}>
      <Icon name="info" />
      <span className="resui-banner-text">
        {text}{' '}
        <Link className="resui-link" to={href}>{REVIEW_LINK_TEXT}</Link>
      </span>
    </div>
  );
};

export default ReviewRequiredBanner;
